import { useEffect, useMemo, useState } from 'react';

import { useToast } from './Toast';
import {
  getAdminDbTables,
  getAdminDbTableRows,
  createAdminDbRow,
  patchAdminDbRow,
  deleteAdminDbRow,
} from '../lib/admin';

const PAGE_SIZES = [25, 50, 100, 200];
const TAIL_COLUMNS = ['created_at', 'updated_at'];

function formatCell(value) {
  if (value === null || value === undefined) return { text: 'null', cls: 'text-gray-400 italic' };
  if (typeof value === 'object') return { text: JSON.stringify(value), cls: 'font-mono text-xs text-gray-700' };
  if (typeof value === 'boolean') return { text: String(value), cls: 'font-mono' };
  return { text: String(value), cls: '' };
}

function cellSearchString(value) {
  if (value === null || value === undefined) return '';
  return typeof value === 'object' ? JSON.stringify(value) : String(value);
}

// Null-last, numeric when both look numeric, else locale string compare.
function compareValues(a, b) {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  const na = Number(a);
  const nb = Number(b);
  const numeric = !Number.isNaN(na) && !Number.isNaN(nb)
    && String(a).trim() !== '' && String(b).trim() !== '';
  if (numeric) return na - nb;
  return String(a).localeCompare(String(b));
}

export default function AdminDbTable() {
  const { showToast } = useToast();

  const [tables, setTables] = useState([]);
  const [table, setTable] = useState('');
  const [tablesError, setTablesError] = useState(null);

  const [rows, setRows] = useState([]);
  const [primaryKey, setPrimaryKey] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rowsError, setRowsError] = useState(null);

  const [offset, setOffset] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  const [filter, setFilter] = useState('');
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [selected, setSelected] = useState(() => new Set());
  const [mutating, setMutating] = useState(false);

  const [editPk, setEditPk] = useState(null);
  const [editJson, setEditJson] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [createJson, setCreateJson] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const resp = await getAdminDbTables();
        const list = Array.isArray(resp?.tables) ? resp.tables : [];
        setTables(list);
        setTablesError(null);
        if (list[0]?.name) setTable(String(list[0].name));
      } catch {
        setTablesError('Failed to load tables');
      }
    })();
  }, []);

  const loadRows = async () => {
    if (!table) return;
    setLoading(true);
    setRowsError(null);
    setSelected(new Set());
    try {
      const resp = await getAdminDbTableRows(table, { offset, limit: pageSize });
      setRows(Array.isArray(resp?.rows) ? resp.rows : []);
      setPrimaryKey(resp?.primaryKey ?? null);
      setHasMore(Boolean(resp?.hasMore));
    } catch {
      setRows([]);
      setPrimaryKey(null);
      setHasMore(false);
      setRowsError('Failed to load rows');
    } finally {
      setLoading(false);
    }
  };

  // Reset to first page when table or page size changes.
  useEffect(() => { setOffset(0); }, [table, pageSize]);
  useEffect(() => { loadRows(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [table, offset, pageSize]);

  const columns = useMemo(() => {
    if (!rows.length) return [];
    const keys = Object.keys(rows[0]);
    return [
      ...(primaryKey && keys.includes(primaryKey) ? [primaryKey] : []),
      ...keys.filter((k) => k !== primaryKey && !TAIL_COLUMNS.includes(k)),
      ...keys.filter((k) => TAIL_COLUMNS.includes(k)),
    ];
  }, [rows, primaryKey]);

  const view = useMemo(() => {
    let result = rows;
    const needle = filter.trim().toLowerCase();
    if (needle) {
      result = result.filter((row) =>
        columns.some((c) => cellSearchString(row[c]).toLowerCase().includes(needle))
      );
    }
    if (sortKey) {
      result = [...result].sort((a, b) => {
        const cmp = compareValues(a[sortKey], b[sortKey]);
        return sortDir === 'asc' ? cmp : -cmp;
      });
    }
    return result;
  }, [rows, columns, filter, sortKey, sortDir]);

  const pkOf = (row) => (primaryKey ? row[primaryKey] : null);

  const toggleSort = (col) => {
    if (sortKey !== col) { setSortKey(col); setSortDir('asc'); return; }
    if (sortDir === 'asc') { setSortDir('desc'); return; }
    setSortKey(null);
    setSortDir('asc');
  };

  const allVisibleSelected = view.length > 0 && view.every((row) => selected.has(String(pkOf(row))));
  const toggleSelectAll = () => {
    const next = new Set(selected);
    if (allVisibleSelected) {
      view.forEach((row) => next.delete(String(pkOf(row))));
    } else {
      view.forEach((row) => { const k = pkOf(row); if (k != null) next.add(String(k)); });
    }
    setSelected(next);
  };
  const toggleSelect = (pk) => {
    const next = new Set(selected);
    const key = String(pk);
    if (next.has(key)) next.delete(key); else next.add(key);
    setSelected(next);
  };

  const onDelete = async (pk) => {
    if (pk == null) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete ${primaryKey}=${pk}?`)) return;
    setMutating(true);
    try {
      await deleteAdminDbRow(table, String(pk));
      await loadRows();
      showToast('Row deleted', { kind: 'success' });
    } catch {
      showToast('Delete failed', { kind: 'error' });
    } finally {
      setMutating(false);
    }
  };

  const onBulkDelete = async () => {
    if (!selected.size || !primaryKey) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete ${selected.size} selected row(s)? This cannot be undone.`)) return;
    setMutating(true);
    let ok = 0;
    let fail = 0;
    for (const pk of selected) {
      try { await deleteAdminDbRow(table, pk); ok += 1; } catch { fail += 1; }
    }
    await loadRows();
    setMutating(false);
    showToast(`Deleted ${ok}${fail ? `, ${fail} failed` : ''}`, { kind: fail ? 'error' : 'success' });
  };

  const openEdit = (row) => {
    setEditPk(String(pkOf(row)));
    const editable = { ...row };
    delete editable[primaryKey];
    TAIL_COLUMNS.forEach((c) => delete editable[c]);
    setEditJson(JSON.stringify(editable, null, 2));
  };
  const saveEdit = async () => {
    setMutating(true);
    try {
      const patch = JSON.parse(editJson || '{}');
      await patchAdminDbRow(table, editPk, patch);
      setEditPk(null);
      setEditJson('');
      await loadRows();
      showToast('Row updated', { kind: 'success' });
    } catch {
      showToast('Update failed (invalid JSON or API error)', { kind: 'error' });
    } finally {
      setMutating(false);
    }
  };

  const saveCreate = async () => {
    setMutating(true);
    try {
      const obj = JSON.parse(createJson || '{}');
      await createAdminDbRow(table, obj);
      setCreateOpen(false);
      setCreateJson('');
      setOffset(0);
      await loadRows();
      showToast('Row created', { kind: 'success' });
    } catch {
      showToast('Create failed (invalid JSON or API error)', { kind: 'error' });
    } finally {
      setMutating(false);
    }
  };

  const sortIndicator = (col) => {
    if (sortKey !== col) return <span className="text-gray-300">↕</span>;
    return <span className="text-NBNA-primary">{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  return (
    <div className="rounded-lg bg-white shadow">
      {/* Toolbar */}
      <div className="flex flex-wrap items-end gap-3 border-b p-4">
        <div>
          <label className="block text-xs font-medium text-gray-500">Table</label>
          <select
            className="mt-1 rounded border border-gray-300 p-2 text-sm"
            value={table}
            onChange={(e) => setTable(e.target.value)}
          >
            {tables.map((t) => (
              <option key={t.name} value={t.name}>{t.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500">Page size</label>
          <select
            className="mt-1 rounded border border-gray-300 p-2 text-sm"
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
          >
            {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <div className="flex-1 min-w-[180px]">
          <label className="block text-xs font-medium text-gray-500">Filter (this page)</label>
          <input
            type="text"
            className="mt-1 w-full rounded border border-gray-300 p-2 text-sm"
            placeholder="Search across columns…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
        <button type="button" className="btn-secondary" onClick={loadRows} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
        <button type="button" className="btn-primary" onClick={() => { setCreateJson('{\n  \n}'); setCreateOpen(true); }} disabled={!table}>
          New row
        </button>
        <button
          type="button"
          className="rounded-md border border-red-300 bg-white px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-40"
          onClick={onBulkDelete}
          disabled={!selected.size || mutating}
        >
          Delete selected ({selected.size})
        </button>
      </div>

      {tablesError ? <div className="p-4 text-sm text-red-700">{tablesError}</div> : null}
      {rowsError ? <div className="p-4 text-sm text-red-700">{rowsError}</div> : null}

      {/* Table */}
      <div className="overflow-auto" style={{ maxHeight: 620 }}>
        <table className="min-w-full border-collapse text-sm">
          <thead className="sticky top-0 z-10 bg-gray-50 shadow-sm">
            <tr>
              <th className="w-10 border-b px-3 py-2">
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={toggleSelectAll}
                  disabled={!primaryKey || !view.length}
                  aria-label="Select all rows"
                />
              </th>
              {columns.map((col) => (
                <th
                  key={col}
                  className="cursor-pointer select-none whitespace-nowrap border-b px-3 py-2 text-left font-semibold text-gray-700 hover:bg-gray-100"
                  onClick={() => toggleSort(col)}
                  title="Click to sort"
                >
                  <span className="inline-flex items-center gap-1">
                    {col}
                    {col === primaryKey ? <span className="text-[10px] uppercase text-NBNA-primary/70">pk</span> : null}
                    {sortIndicator(col)}
                  </span>
                </th>
              ))}
              <th className="border-b px-3 py-2 text-right font-semibold text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={columns.length + 2} className="px-3 py-6 text-center text-gray-500">Loading…</td></tr>
            ) : view.length === 0 ? (
              <tr><td colSpan={(columns.length || 1) + 2} className="px-3 py-6 text-center text-gray-500">No rows.</td></tr>
            ) : (
              view.map((row) => {
                const pk = pkOf(row);
                const pkStr = pk == null ? '' : String(pk);
                const isSelected = selected.has(pkStr);
                return (
                  <tr key={pkStr || JSON.stringify(row).slice(0, 48)} className={isSelected ? 'bg-NBNA-primary/5' : 'hover:bg-gray-50'}>
                    <td className="border-b px-3 py-2 align-top">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelect(pk)}
                        disabled={!primaryKey}
                        aria-label="Select row"
                      />
                    </td>
                    {columns.map((col) => {
                      const { text, cls } = formatCell(row[col]);
                      return (
                        <td key={col} className="max-w-[320px] truncate border-b px-3 py-2 align-top" title={text}>
                          <span className={cls}>{text}</span>
                        </td>
                      );
                    })}
                    <td className="whitespace-nowrap border-b px-3 py-2 text-right align-top">
                      <button type="button" className="text-NBNA-primary hover:underline" onClick={() => openEdit(row)} disabled={!primaryKey}>Edit</button>
                      <button type="button" className="ml-3 text-red-600 hover:underline" onClick={() => onDelete(pk)} disabled={!primaryKey || mutating}>Delete</button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Footer / pagination */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-t p-3 text-sm text-gray-600">
        <div>
          Showing {view.length}{filter.trim() ? ` of ${rows.length} loaded` : ''} row{view.length === 1 ? '' : 's'}
          {primaryKey ? null : <span className="ml-2 text-amber-700">(no primary key — edit/select disabled)</span>}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setOffset(Math.max(0, offset - pageSize))}
            disabled={offset === 0 || loading}
          >
            ← Prev
          </button>
          <span className="px-1">rows {offset + 1}–{offset + rows.length}</span>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setOffset(offset + pageSize)}
            disabled={!hasMore || loading}
          >
            Next →
          </button>
        </div>
      </div>

      {/* Edit modal */}
      {editPk != null ? (
        <Modal title={`Edit row · ${primaryKey}=${editPk}`} onClose={() => { setEditPk(null); setEditJson(''); }}>
          <p className="mb-2 text-xs text-gray-500">Send only the fields to update (JSON).</p>
          <textarea
            className="h-64 w-full rounded border border-gray-300 p-2 font-mono text-xs"
            value={editJson}
            onChange={(e) => setEditJson(e.target.value)}
          />
          <div className="mt-3 flex justify-end gap-2">
            <button type="button" className="btn-secondary" onClick={() => { setEditPk(null); setEditJson(''); }}>Cancel</button>
            <button type="button" className="btn-primary" onClick={saveEdit} disabled={mutating}>Save patch</button>
          </div>
        </Modal>
      ) : null}

      {/* Create modal */}
      {createOpen ? (
        <Modal title={`New row · ${table}`} onClose={() => setCreateOpen(false)}>
          <p className="mb-2 text-xs text-gray-500">Row as JSON (omit auto-managed columns).</p>
          <textarea
            className="h-64 w-full rounded border border-gray-300 p-2 font-mono text-xs"
            placeholder='{"column": "value"}'
            value={createJson}
            onChange={(e) => setCreateJson(e.target.value)}
          />
          <div className="mt-3 flex justify-end gap-2">
            <button type="button" className="btn-secondary" onClick={() => setCreateOpen(false)}>Cancel</button>
            <button type="button" className="btn-primary" onClick={saveCreate} disabled={mutating}>Create</button>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-2xl rounded-lg bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-[#0E4069]">{title}</h3>
          <button type="button" className="text-gray-400 hover:text-gray-700" onClick={onClose} aria-label="Close">✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}
